import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { makePuzzle, checkWin, findConflicts } from "@/lib/takuzu";

export function TakuzuGame({ n, givenRatio, onWin, resetKey }) {
  const { t } = useTranslation();
  const [grid, setGrid] = useState([]);
  const [given, setGiven] = useState([]);

  const start = useCallback(() => {
    const p = makePuzzle(n, givenRatio);
    setGrid(p.puzzle.map((r) => r.slice()));
    setGiven(p.puzzle.map((r) => r.map((v) => v !== -1)));
  }, [n, givenRatio]);

  useEffect(() => { start(); }, [start, resetKey]);

  const conflicts = useMemo(() => (grid.length ? findConflicts(grid, n) : new Set()), [grid, n]);
  const solved = useMemo(() => (grid.length ? checkWin(grid, n) : false), [grid, n]);

  useEffect(() => { if (solved) onWin?.(); }, [solved, onWin]);

  const cycle = (r, c) => {
    if (given[r][c] || solved) return;
    setGrid((g) => {
      const ng = g.map((x) => x.slice());
      ng[r][c] = ng[r][c] === -1 ? 0 : ng[r][c] === 0 ? 1 : -1;
      return ng;
    });
  };

  if (!grid.length) return null;

  return (
    <div className="flex flex-col items-center" data-testid="takuzu-game">
      <p className="text-xs text-foreground/45 text-center max-w-md mb-6 leading-relaxed">{t("games.takuzuRules")}</p>
      <div
        className="grid gap-1.5 mx-auto"
        style={{ gridTemplateColumns: `repeat(${n}, minmax(0,1fr))`, width: "min(88vw, 420px)" }}
        data-testid="takuzu-grid"
      >
        {grid.map((row, r) =>
          row.map((v, c) => {
            const isGiven = given[r][c];
            const bad = conflicts.has(`${r},${c}`);
            return (
              <button
                key={`${r}-${c}`}
                data-testid={`takuzu-cell-${r}-${c}`}
                onClick={() => cycle(r, c)}
                disabled={isGiven}
                className={`aspect-square rounded-lg flex items-center justify-center font-display text-lg sm:text-xl transition-all duration-200 border
                  ${v === -1 ? "bg-white/[0.03] border-white/10 hover:border-primary/40" : "border-transparent"}
                  ${v !== -1 && !bad ? (isGiven ? "bg-primary/20 text-primary" : "bg-white/[0.06] text-foreground/85") : ""}
                  ${bad ? "bg-red-500/20 text-red-300 border-red-500/40" : ""}`}
              >
                {v === -1 ? "" : v}
              </button>
            );
          })
        )}
      </div>
      <button
        onClick={start}
        data-testid="takuzu-new-btn"
        className="mt-7 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/45 hover:text-primary transition-colors"
      >
        <RefreshCw size={13} /> {t("games.newGrid")}
      </button>
    </div>
  );
}
